import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Dialog } from "@/Dialog";
import { hasTokens } from "%/token";
import {
  agreeTerms,
  getPendingTerms,
  TERMS_TYPE_LABEL,
  type TermsDocument,
} from "~/services/common/terms";

/**
 * 약관 재동의 게이트.
 *
 *  - 로그인 상태에서 "현행 필수 약관 중 내 동의 기록이 없는 것"이 있으면 모달을 띄운다.
 *  - 동의 로그가 INFO_TERMS_SEQ(=버전)를 들고 있어서 별도 상태 저장 없이 판정된다.
 *  - 나중에 하기로 닫아도 다음 진입 시 다시 뜬다 (세션 동안만 숨김).
 *    필수 약관 미동의를 이유로 앱을 잠그지는 않는다 — 이용계약 해지 안내로 갈음.
 */
export function TermsConsentGate() {
  const { history } = useRouter();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);

  const loggedIn = hasTokens();

  const { data: pending } = useQuery({
    queryKey: ["terms-pending"],
    queryFn: getPendingTerms,
    enabled: loggedIn,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const targets = useMemo(() => pending ?? [], [pending]);

  const agreeMutation = useMutation({
    mutationFn: () => agreeTerms(targets.map((t) => t.id)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["terms-pending"] });
    },
  });

  const openPolicy = useCallback(
    (doc: TermsDocument) => {
      history.push(
        `/policy?type=${doc.termsType}&seq=${doc.id}`,
      );
    },
    [history],
  );

  if (!loggedIn || dismissed || targets.length === 0) return null;

  return (
    <Dialog visible persist={false} onClose={() => setDismissed(true)}>
      <div className="flex flex-col w-full">
        <h2 className="mb-1 text-lg font-bold text-center">약관이 개정되었어요</h2>
        <p className="mb-4 text-sm text-center text-[#666666]">
          변경된 약관에 동의해야 서비스를 계속 이용할 수 있어요.
        </p>

        <ul className="mb-4 divide-y divide-gray-100 rounded-xl border border-[#E9E9E9]">
          {targets.map((doc) => (
            <li
              key={doc.id}
              className="flex justify-between items-center px-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  [필수] {TERMS_TYPE_LABEL[doc.termsType]}
                </p>
                <p className="text-xs text-[#999999]">
                  {doc.termsVersion} · 시행일{" "}
                  {doc.effectiveAt?.slice(0, 10) ?? "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => openPolicy(doc)}
                className="shrink-0 ml-2 text-xs text-orange-500 underline"
              >
                보기
              </button>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => agreeMutation.mutate()}
          disabled={agreeMutation.isPending}
          className="py-3 w-full text-sm font-bold text-white bg-orange-400 rounded-xl disabled:opacity-60"
        >
          {agreeMutation.isPending ? "처리 중..." : "모두 동의하고 계속하기"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="py-2.5 w-full text-sm text-[#999999]"
        >
          나중에 하기
        </button>

        <p className="mt-1 text-[11px] text-center text-[#999999]">
          동의하지 않으시면 이용계약을 해지할 수 있어요.
        </p>
      </div>
    </Dialog>
  );
}

export default TermsConsentGate;
